import React, { createContext, useContext, useState, useEffect } from 'react';
import { loginRequest, buildApiUrl, API_CONFIG } from '../utils/api';

const AuthContext = createContext();

// Función para decodificar el JWT y extraer el payload
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Cargar usuario desde localStorage al iniciar
    const savedUser = localStorage.getItem('user');
    const savedToken = localStorage.getItem('accessToken');
    
    if (savedUser && savedToken) {
      try {
        const parsedUser = JSON.parse(savedUser);
        
        // Decodificar el token para verificar y actualizar el role
        const tokenPayload = decodeToken(savedToken);
        
        if (tokenPayload) {
          // Actualizar el usuario con datos del token si es necesario
          parsedUser.role = tokenPayload.role || parsedUser.role || 'user';
          parsedUser.corporationId = tokenPayload.corporationId || parsedUser.corporationId;
          parsedUser.email = tokenPayload.user || parsedUser.email;
          
          // Guardar el usuario actualizado
          localStorage.setItem('user', JSON.stringify(parsedUser));
        }
        
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      
      const data = await loginRequest(buildApiUrl(API_CONFIG.ENDPOINTS.LOGIN), {
        method: 'POST',
        body: JSON.stringify({
          email,
          password
        })
      });
      
      if (data.response && data.response.content && data.response.content.accessToken) {
        const { accessToken } = data.response.content;
        
        // Decodificar el token para extraer el payload
        const tokenPayload = decodeToken(accessToken);
        
        if (!tokenPayload) {
          return { success: false, error: 'Error al decodificar el token' };
        }
        
        // Guardar token en localStorage
        localStorage.setItem('accessToken', accessToken);
        
        // Crear objeto de usuario con datos del token
        const userData = {
          id: tokenPayload.corporationId || '1',
          email: tokenPayload.user || email,
          name: (tokenPayload.user || email).split('@')[0],
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face',
          role: tokenPayload.role || 'user', // Extraer role del token
          corporationId: tokenPayload.corporationId,
          team: 'Development',
          methodology: 'scrum',
          operationId: data.operationId
        };
        
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        
        return { success: true, data: data.response };
      } else {
        // Si el backend respondió pero sin el formato esperado
        return { success: false, error: 'Respuesta inesperada del servidor' };
      }
    } catch (error) {
      // Capturar y devolver el error sin propagarlo más
      console.error('Login error:', error);
      
      // Asegurarnos de devolver un objeto con success: false
      return { 
        success: false, 
        error: error.message || 'Usuario o contraseña inválidos' 
      };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      
      // Por ahora usar el endpoint de login para registro también
      // Puedes cambiar esto cuando tengas el endpoint de registro
      const data = await loginRequest(buildApiUrl(API_CONFIG.ENDPOINTS.REGISTER), {
        method: 'POST',
        body: JSON.stringify({
          email: userData.email,
          password: userData.password,
          name: userData.name,
          team: userData.team
        })
      });
      
      if (data.response && data.response.content && data.response.content.accessToken) {
        const { accessToken } = data.response.content;
        
        // Decodificar el token para extraer el payload
        const tokenPayload = decodeToken(accessToken);
        
        if (!tokenPayload) {
          return { success: false, error: 'Error al decodificar el token' };
        }
        
        localStorage.setItem('accessToken', accessToken);
        
        const newUser = {
          id: tokenPayload.corporationId || '1',
          email: tokenPayload.user || userData.email,
          name: userData.name,
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face',
          role: tokenPayload.role || 'user', // Extraer role del token
          corporationId: tokenPayload.corporationId,
          team: userData.team || 'Development',
          methodology: 'scrum',
          operationId: data.operationId
        };
        
        setUser(newUser);
        localStorage.setItem('user', JSON.stringify(newUser));
        
        return { success: true, data: data.response };
      } else {
        throw new Error('Token not found in response');
      }
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const socialLogin = async (provider) => {
    try {
      setLoading(true);
      
      // Por ahora simular login social (puedes implementar OAuth más adelante)
      const mockUser = {
        id: '1',
        email: `user@${provider}.com`,
        name: `Usuario ${provider}`,
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face',
        role: 'admin',
        team: 'Development',
        methodology: 'scrum'
      };
      
      // Simular token para login social
      const mockToken = 'social-login-token-' + Date.now();
      localStorage.setItem('accessToken', mockToken);
      
      setUser(mockUser);
      localStorage.setItem('user', JSON.stringify(mockUser));
      
      return { success: true };
    } catch (error) {
      console.error('Social login error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // Función para verificar si el usuario está autenticado
  const isAuthenticated = () => {
    const token = localStorage.getItem('accessToken');
    const savedUser = localStorage.getItem('user');
    return !!(token && savedUser);
  };

  // Función para obtener el token actual
  const getToken = () => {
    return localStorage.getItem('accessToken');
  };

  const value = {
    user,
    loading,
    login,
    register,
    socialLogin,
    logout,
    updateUser,
    isAuthenticated,
    getToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
